/**
 * Aspect Admin — Support / feedback / internal notes.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, toast, openDrawer, dropdownHtml, bindDropdowns } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { TICKETS } from "../mock_data.js";

function prioBadge(p) {
  const map = { high: ["err", "High"], medium: ["warn", "Medium"], low: ["mute", "Low"] };
  const [kind, label] = map[p] ?? ["mute", p];
  return `<span class="ap-badge ap-badge--${kind}">${t(label)}</span>`;
}

function ticketDrawer(root, tk) {
  openDrawer(root, {
    title: t(tk.subject),
    subtitle: t("{f} · updated {u}", { f: tk.from, u: t(tk.updated) }),
    bodyHtml: `
      <div class="ap-row" style="gap:0.5rem;">${statusBadge(tk.status)} ${prioBadge(tk.priority)}</div>
      <div class="ap-drawer__section">
        <h4>${t("Conversation")}</h4>
        <div class="ap-stack" style="gap:0.7rem;">
          <div class="ap-panel ap-panel--pad" style="padding:0.85rem 1rem;">
            <p style="margin:0 0 0.3rem;font-size:0.74rem;font-weight:700;color:var(--ap-mist);">${escapeHtml(tk.from)} · ${t(tk.updated)}</p>
            <p style="margin:0;font-size:0.83rem;color:var(--ap-slate);line-height:1.55;">${t("Hi — {s}. Could you take a look? This affects our client demos scheduled for next week.", { s: escapeHtml(t(tk.subject)) })}</p>
          </div>
          <div class="ap-insight" style="padding:0.85rem 1rem;">
            <div class="ap-insight__head">${icon("sparkles", 14)}<span class="ap-insight__tag">${t("Suggested reply")}</span></div>
            <p class="ap-insight__body">${t("Thanks for flagging this. We've reproduced the issue and a fix is rolling out with tomorrow's release. As a workaround, re-exporting the tour with \"high quality textures\" enabled resolves the washed-out look.")}</p>
            <div class="ap-insight__foot"><span></span><button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-use-reply>${t("Use this reply")}</button></div>
          </div>
        </div>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Internal notes")}</h4>
        <textarea class="ap-textarea" placeholder="${t("Visible to your team only…")}">${t("Likely related to the tone-mapping change in v2.14.")}</textarea>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Properties")}</h4>
        <dl class="ap-def">
          <dt>${t("Assignee")}</dt><dd>${escapeHtml(tk.assignee)}</dd>
          <dt>${t("Priority")}</dt><dd>${prioBadge(tk.priority)}</dd>
          <dt>${t("Status")}</dt><dd>${statusBadge(tk.status)}</dd>
        </dl>
      </div>
    `,
    footHtml: `
      <button type="button" class="ap-btn" data-resolve>${icon("check", 15)} ${t("Mark resolved")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-reply>${icon("send", 15)} ${t("Reply")}</button>
    `,
  });
  const slot = root.querySelector("#ap-overlay-slot");
  slot?.querySelector("[data-use-reply]")?.addEventListener("click", () => toast(root, t("Suggested reply inserted into composer"), "ok"));
  slot?.querySelector("[data-resolve]")?.addEventListener("click", () => toast(root, t("Ticket marked resolved"), "ok"));
  slot?.querySelector("[data-reply]")?.addEventListener("click", () => toast(root, t("Reply sent"), "ok"));
}

export function renderSupport(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const open = TICKETS.filter((tk) => tk.status !== "resolved").length;

  const content = `
    ${pageHead(t("Support"), t("{n} open conversations · median first response 42 minutes.", { n: open }), `
      <button type="button" class="ap-btn" id="su-docs">${icon("doc", 15)} ${t("Documentation")}</button>
      <button type="button" class="ap-btn ap-btn--primary" id="su-new">${icon("plus", 15)} ${t("New ticket")}</button>
    `)}

    <div class="ap-grid ap-grid--main">
      <div class="ap-panel">
        <div class="ap-panel__head">
          <h3 class="ap-panel__title">${t("Client conversations")}</h3>
        </div>
        <div class="ap-tablewrap ap-scroll">
          <table class="ap-table">
            <thead><tr><th>${t("Subject")}</th><th>${t("From")}</th><th>${t("Status")}</th><th>${t("Priority")}</th><th>${t("Assignee")}</th><th>${t("Updated")}</th></tr></thead>
            <tbody>
              ${TICKETS.map(
                (tk) => `
                <tr data-row data-ticket="${tk.id}">
                  <td class="ap-cell-main">${escapeHtml(t(tk.subject))}</td>
                  <td>${escapeHtml(tk.from)}</td>
                  <td>${statusBadge(tk.status)}</td>
                  <td>${prioBadge(tk.priority)}</td>
                  <td>${escapeHtml(tk.assignee)}</td>
                  <td>${t(tk.updated)}</td>
                </tr>`
              ).join("")}
            </tbody>
          </table>
        </div>
      </div>

      <div class="ap-stack">
        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Send us feedback")}</h3></div>
          <div class="ap-stack" style="gap:0.8rem;">
            <div class="ap-field">
              <label class="ap-field__label">${t("What's on your mind?")}</label>
              ${dropdownHtml({
                value: t("Feature request"),
                options: [t("Feature request"), t("Bug report"), t("Pricing question"), t("Something else")],
              })}
            </div>
            <div class="ap-field">
              <textarea class="ap-textarea" placeholder="${t("Tell us what would make Aspect better for your team…")}"></textarea>
            </div>
            <button type="button" class="ap-btn ap-btn--primary" id="su-send" style="align-self:flex-end;">${icon("send", 15)} ${t("Send feedback")}</button>
          </div>
        </div>

        <div class="ap-panel ap-panel--pad">
          <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Resources")}</h3></div>
          <div class="ap-stack" style="gap:0.35rem;">
            ${[
              ["doc", "Getting started guide", "Set up your first AI-staged tour"],
              ["cube", "3D editor handbook", "Tracing, staging, and publishing"],
              ["key", "API reference", "REST + webhooks documentation"],
              ["shield", "Security & compliance", "GDPR, data residency, SLAs"],
            ]
              .map(
                ([ic, title, desc]) => `
              <button type="button" class="ap-pop__item" data-res style="padding:0.6rem 0.65rem;">
                ${icon(ic, 16)}
                <span style="flex:1;">
                  <strong style="display:block;font-size:0.83rem;color:var(--ap-ink);">${t(title)}</strong>
                  <span style="font-size:0.72rem;color:var(--ap-mist);">${t(desc)}</span>
                </span>
                ${icon("chevronRight", 14)}
              </button>`
              )
              .join("")}
          </div>
        </div>
      </div>
    </div>
  `;

  const el = renderShell(root, "/support", content);
  if (!el) return;

  el.querySelector("#su-new")?.addEventListener("click", () => toast(root, t("New ticket composer opening…"), "info"));
  el.querySelector("#su-docs")?.addEventListener("click", () => toast(root, t("Documentation opens in a new tab"), "info"));
  el.querySelector("#su-send")?.addEventListener("click", () => toast(root, t("Thank you — feedback sent to the product team"), "ok"));
  el.querySelectorAll("[data-res]").forEach((b) => b.addEventListener("click", () => toast(root, t("Opening resource…"), "info")));
  bindDropdowns(el);

  el.querySelector("tbody")?.addEventListener("click", (e) => {
    const row = e.target.closest("[data-ticket]");
    if (!row) return;
    const tk = TICKETS.find((x) => x.id === row.dataset.ticket);
    if (tk) ticketDrawer(root, tk);
  });
}
